import { useCallback, useEffect, useMemo, useReducer } from "react";

import type { OrderAttributesFieldValue } from "@jti/commerce-types";
import { newrelic } from "@jti/fe-clients";
import { isNonEmptyString } from "@jti/fe-common";
import type { MagentoShippingMethod } from "@jti/magento-types";
import { checkoutV2Slice } from "@jti/store/slices/checkoutV2Slice";
import {
  useSetCheckoutFastFlowCartMutation,
  useSetPaymentMethodOnCartMutation,
  useSetShippingAddressOnCartWithMethodsMutation,
} from "@jti/store/slices/magentoApiSlice";
import { useUpdateCustomAttributesMutation } from "@jti/store/slices/profileApiSlice";

import { FISCAL_CODE_ATTRIBUTE_KEY } from "@checkout-v2/features/OrderAttributes/const";
import { telephoneFieldFactory } from "@checkout-v2/formFields";
import { constructBillingAddressPayload } from "@checkout-v2/forms/ShippingForm/utils";
import { profileAddressToMagentoAddressAdapter } from "@checkout-v2/forms/ShippingForm/utils/profileAddressToMagentoAddressAdapter";
import { useGtmPaymentInfo, useGtmShippingInfo } from "@checkout-v2/shared/hooks";
import { useCountry } from "@checkout-v2/shared/hooks/useCountry";
import { sanitizeAddressRegionData } from "@checkout-v2/shared/utils/sanitizeAddressRegionData";

import { CheckoutFastFlowSkipError } from "../checkoutFastFlowSkipError";
import { checkoutFastFlowLogger } from "../debug";
import { checkoutFastFlowReducer } from "../reducer";
import type { FastFlowPaymentStepCandidateReady, FastFlowShippingStepCandidateReady } from "../utils";
import {
  buildPaymentStepFastFlowCandidate,
  buildShippingStepFastFlowCandidate,
  isFastFlowPaymentStepCandidateReady,
  isFastFlowShippingStepCandidateReady,
} from "../utils";

import type { UseCheckoutFastFlowArgs } from "./useCheckoutFastFlow.types";

export const useCheckoutFastFlow = ({
  ready,
  initialDefaultValues,
  hasDefaultAddress,
  checkoutConfiguration,
  allDeliveryOptions,
  isSubscription,
  dispatch,
  lastOrderData,
  definedOrderAttributes,
  paymentOptions,
  validatePhone,
  cartId,
  skip,
  isBillingAddressVisibleForCheckout,
  hasProfileFiscalCode,
  isShippingAgeVerificationEnabled,
  isAgeVerified,
  checkoutFlowType,
  shippingInfoPrefilled,
  paymentInfoPrefilled,
}: UseCheckoutFastFlowArgs) => {
  const [setCheckoutFastFlowFlowCart] = useSetCheckoutFastFlowCartMutation();
  const [setPaymentMethod] = useSetPaymentMethodOnCartMutation();
  const [setShippingAddressOnCartWithMethods] = useSetShippingAddressOnCartWithMethodsMutation();
  const [updateCustomAttributes] = useUpdateCustomAttributesMutation();
  const countryQueryState = useCountry();
  const { trackGtmShippingInfo } = useGtmShippingInfo();
  const { trackGtmPaymentInfo } = useGtmPaymentInfo();

  const [state, dispatchState] = useReducer(checkoutFastFlowReducer, "idle");

  const paymentStepCandidate = useMemo(() => {
    return buildPaymentStepFastFlowCandidate({
      lastOrderData,
      paymentOptions,
    });
  }, [lastOrderData, paymentOptions]);

  const ensurePhoneValid = useCallback(
    async (phoneNumber: string): Promise<void> => {
      if (!phoneNumber) {
        checkoutFastFlowLogger("SKIP: Missing phone number");
        throw new CheckoutFastFlowSkipError("Missing phone number");
      }

      const result = await validatePhone(phoneNumber);

      if (!result.isValid) {
        checkoutFastFlowLogger("SKIP: Phone validation failed");
        throw new CheckoutFastFlowSkipError("Phone validation failed");
      }
    },
    [validatePhone]
  );

  const ensureFiscalCodeValid = useCallback(
    async (fiscalCode?: OrderAttributesFieldValue): Promise<void> => {
      if (!isNonEmptyString(fiscalCode)) {
        return;
      }

      try {
        await updateCustomAttributes({ [FISCAL_CODE_ATTRIBUTE_KEY]: fiscalCode.trim() }).unwrap();
      } catch (error) {
        checkoutFastFlowLogger("SKIP: Fiscal code validation failed", error);
        throw new CheckoutFastFlowSkipError("Fiscal code validation failed");
      }
    },
    [updateCustomAttributes]
  );

  const dispatchShippingGtmEvent = useCallback(
    (deliveryOption: string, deliveryOptions: MagentoShippingMethod[]): void => {
      const selectedShippingMethod = deliveryOptions.find(
        (option: MagentoShippingMethod) => option.methodCode === deliveryOption
      );
      if (selectedShippingMethod) {
        trackGtmShippingInfo(selectedShippingMethod, checkoutFlowType, shippingInfoPrefilled);
      }
    },
    [checkoutFlowType, shippingInfoPrefilled, trackGtmShippingInfo]
  );

  const dispatchPaymentGtmEvent = useCallback(
    (paymentMethodCode: string): void => {
      trackGtmPaymentInfo(paymentMethodCode, checkoutFlowType, paymentInfoPrefilled);
    },
    [checkoutFlowType, paymentInfoPrefilled, trackGtmPaymentInfo]
  );

  const completeShippingSection = useCallback(
    (candidateData: FastFlowShippingStepCandidateReady, deliveryOptions: MagentoShippingMethod[]): void => {
      dispatchShippingGtmEvent(candidateData.deliveryOption, deliveryOptions);

      dispatch(
        checkoutV2Slice.actions.setSectionsState({
          shippingSection: "completed",
          paymentSection: "active",
          orderConfirmationSection: "rested",
        })
      );

      dispatch(checkoutV2Slice.actions.setData(candidateData));
    },
    [dispatch, dispatchShippingGtmEvent]
  );

  const completeCheckoutFastFlowFlow = useCallback(
    (
      candidateData: FastFlowShippingStepCandidateReady &
        Omit<FastFlowPaymentStepCandidateReady, "magentoPaymentMethodType">,
      deliveryOptions: MagentoShippingMethod[]
    ): void => {
      dispatchShippingGtmEvent(candidateData.deliveryOption, deliveryOptions);
      dispatchPaymentGtmEvent(candidateData.paymentMethodCode);

      dispatch(
        checkoutV2Slice.actions.setSectionsState({
          shippingSection: "completed",
          paymentSection: "completed",
          orderConfirmationSection: "active",
        })
      );

      dispatch(checkoutV2Slice.actions.setData(candidateData));
    },
    [dispatch, dispatchShippingGtmEvent, dispatchPaymentGtmEvent]
  );

  const resolveDeliveryOptions = useCallback(async (): Promise<MagentoShippingMethod[]> => {
    if (allDeliveryOptions.length > 0) {
      return allDeliveryOptions;
    }

    if (!cartId || !initialDefaultValues?.addressHomeDelivery || !hasDefaultAddress) {
      checkoutFastFlowLogger("SKIP: Cannot preseed - missing cart or default address");
      throw new CheckoutFastFlowSkipError("Missing cart or default address for preseed");
    }

    checkoutFastFlowLogger("No delivery options - setting shipping address and resolving methods");

    const availableRegions = countryQueryState.data?.availableRegions;
    const sanitizedAddress = sanitizeAddressRegionData(initialDefaultValues.addressHomeDelivery, availableRegions);

    const result = await setShippingAddressOnCartWithMethods({
      cartId,
      address: profileAddressToMagentoAddressAdapter(sanitizedAddress),
    });

    if ("error" in result) {
      checkoutFastFlowLogger("SKIP: Preseed shipping address failed", result.error);
      throw new CheckoutFastFlowSkipError("Preseed shipping address failed");
    }

    if (!result.data.methods.length) {
      checkoutFastFlowLogger("SKIP: No delivery options after setting address");
      throw new CheckoutFastFlowSkipError("No delivery options available");
    }

    checkoutFastFlowLogger("Fetched delivery options", { count: result.data.methods.length });
    return result.data.methods;
  }, [
    allDeliveryOptions,
    cartId,
    initialDefaultValues?.addressHomeDelivery,
    hasDefaultAddress,
    countryQueryState.data?.availableRegions,
    setShippingAddressOnCartWithMethods,
  ]);

  const runCheckoutFastFlow = useCallback(async (): Promise<void> => {
    const resolvedDeliveryOptions = await resolveDeliveryOptions();

    const shippingStepCandidate = buildShippingStepFastFlowCandidate({
      initialDefaultValues,
      hasDefaultAddress,
      allDeliveryOptions: resolvedDeliveryOptions,
      isSubscription,
      checkoutConfiguration,
      lastOrderData,
      definedOrderAttributes,
      isShippingAgeVerificationEnabled,
      isAgeVerified,
    });

    if (!isFastFlowShippingStepCandidateReady(shippingStepCandidate)) {
      checkoutFastFlowLogger("SKIP: Shipping step candidate not ready");
      throw new CheckoutFastFlowSkipError("Missing candidate or form config");
    }

    if (!hasProfileFiscalCode && shippingStepCandidate.orderAttributes?.[FISCAL_CODE_ATTRIBUTE_KEY]) {
      const { [FISCAL_CODE_ATTRIBUTE_KEY]: _, ...restAttributes } = shippingStepCandidate.orderAttributes;
      shippingStepCandidate.orderAttributes = restAttributes;
    }

    if (!cartId) {
      checkoutFastFlowLogger("SKIP: Missing cart ID");
      throw new CheckoutFastFlowSkipError("Missing cart ID");
    }

    const phoneNumber = telephoneFieldFactory.toTelephoneString(
      shippingStepCandidate.telephone,
      shippingStepCandidate.telephoneFallback
    );
    const shippingAddress = shippingStepCandidate.addressHomeDelivery;
    const billingAddress = constructBillingAddressPayload(
      shippingStepCandidate,
      checkoutConfiguration,
      isBillingAddressVisibleForCheckout,
      shippingStepCandidate.telephoneFallback
    );

    await Promise.all([
      ensurePhoneValid(phoneNumber),
      ensureFiscalCodeValid(shippingStepCandidate.orderAttributes?.[FISCAL_CODE_ATTRIBUTE_KEY]),
    ]);

    const selectedMethod = resolvedDeliveryOptions.find(
      (option: MagentoShippingMethod) => option.methodCode === shippingStepCandidate.deliveryOption
    );

    if (!selectedMethod?.carrierCode) {
      checkoutFastFlowLogger("SKIP: Missing carrier code for delivery option", {
        deliveryOption: shippingStepCandidate.deliveryOption,
      });
      throw new CheckoutFastFlowSkipError("Missing carrier code for delivery option");
    }

    const availableRegions = countryQueryState.data?.availableRegions;

    const shippingStepResult = await setCheckoutFastFlowFlowCart({
      cartId,
      shippingAddress: profileAddressToMagentoAddressAdapter(
        sanitizeAddressRegionData(shippingAddress, availableRegions)
      ),
      billingAddress: profileAddressToMagentoAddressAdapter(
        sanitizeAddressRegionData(billingAddress, availableRegions)
      ),
      carrierCode: selectedMethod.carrierCode,
      methodCode: shippingStepCandidate.deliveryOption,
    });

    if ("error" in shippingStepResult) {
      checkoutFastFlowLogger("SKIP: Shipping step mutation failed", shippingStepResult.error);
      throw new CheckoutFastFlowSkipError("Fast-forward shipping step mutation failed");
    }

    if (!isFastFlowPaymentStepCandidateReady(paymentStepCandidate)) {
      checkoutFastFlowLogger("Payment step candidate not ready - completing shipping only");
      completeShippingSection(shippingStepCandidate, resolvedDeliveryOptions);
      return;
    }

    const paymentStepResult = await setPaymentMethod({ cartId, code: paymentStepCandidate.magentoPaymentMethodType });

    const checkoutState = {
      ...shippingStepCandidate,
      paymentMethodCode: paymentStepCandidate.paymentMethodCode,
    };

    if ("error" in paymentStepResult) {
      checkoutFastFlowLogger("Payment step failed - completing shipping only", paymentStepResult.error);
      completeShippingSection(shippingStepCandidate, resolvedDeliveryOptions);
    } else {
      completeCheckoutFastFlowFlow(checkoutState, resolvedDeliveryOptions);
    }
  }, [
    resolveDeliveryOptions,
    initialDefaultValues,
    hasDefaultAddress,
    checkoutConfiguration,
    isSubscription,
    lastOrderData,
    definedOrderAttributes,
    isShippingAgeVerificationEnabled,
    isAgeVerified,
    cartId,
    hasProfileFiscalCode,
    ensurePhoneValid,
    ensureFiscalCodeValid,
    setCheckoutFastFlowFlowCart,
    paymentStepCandidate,
    setPaymentMethod,
    completeShippingSection,
    completeCheckoutFastFlowFlow,
    isBillingAddressVisibleForCheckout,
    countryQueryState.data?.availableRegions,
  ]);

  useEffect(() => {
    if (skip) {
      if (state === "idle" && ready) {
        checkoutFastFlowLogger("Fast flow skipped (skip=true)");
      }
      return;
    }

    if (state === "idle" && ready) {
      dispatchState({ type: "READY" });
      return;
    }

    if (state !== "deciding") {
      return;
    }

    const run = async () => {
      dispatchState({ type: "RUN" });
      try {
        await runCheckoutFastFlow();

        dispatchState({ type: "COMPLETE", result: "success" });
      } catch (error) {
        if (error instanceof CheckoutFastFlowSkipError) {
          checkoutFastFlowLogger("Fast flow result: skip -", error.message);
          newrelic.noticeError(new Error(`Fast-forward: ${error.message}`));
          dispatchState({ type: "COMPLETE", result: "skip" });
        } else {
          checkoutFastFlowLogger("Fast flow result: fail -", error);
          newrelic.noticeError(error as Error);
          dispatchState({ type: "COMPLETE", result: "fail" });
        }
      }
    };

    void run();
  }, [ready, runCheckoutFastFlow, skip, state]);

  /**
   * Fast-forward is "in progress" when:
   * - We're not skipping
   * - We're either waiting for data (idle, not ready) OR actively running (deciding/running)
   */
  const isCheckoutFastFlowInProgress = !skip && (state === "idle" || state === "deciding" || state === "running");

  return { isCheckoutFastFlowInProgress };
};
